// From-scratch CDT layout. "Vertical" is merchandising (full-height x-window), not SpineLevel.orient.
import {
  GeneratedLayout,
  GeneratedPlacement,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "../studioTypes";
import { shelfLayoutItemsOf, shelfLayoutRunsOf } from "../shelfRankSpace";
import { runBayWidthOf, slacksOf } from "../bayShare";

/** A pooling answer for one value of one level. Key: `attr + PATH_SEPARATOR + value`. */
export type BlockOrientation = "vertical" | "pooled";

export interface CdtLayoutOptions {
  verticalDepth?: number;
  /** Facings of its widest pack a column must hold per shelf. */
  minFacingsPerShelf?: number;
  /** Bounded gap-close spill per window per shelf, raw planogram units. Absent, the run's
   *  air slack (472 of a 4800 bay). */
  driftCm?: number;
  closeGaps?: boolean;
  /** Keyed `attr › value`: "vertical" exempts a value from pooling, "pooled" forces it. */
  poolingOverrides?: Readonly<Record<string, BlockOrientation>>;
  /** Inside a window holding more than one value of this attr, bands must be
   * by this attr or the pooled blocks interleave down the shelves. */
  identityAttr?: string;
  priorityOf?: (item: Record<string, string | null>, attr: string) => number;
}

export interface CdtBand {
  value: string;
  shelves: readonly number[];
}

export interface CdtWindow {
  run: number;
  path: readonly string[];
  x: number;
  width: number;
  pooled: boolean;
  bandKey: string | null;
  bands: readonly CdtBand[];
  skuIds: readonly string[];
}

export type CdtPoolReason = "too-narrow" | "too-few-skus" | "answered-pooled";

/** One pooling decision with the numbers it was made on. `too-few-skus` can
 *  never be a column here: a full-height column needs a pack per shelf. */
export interface CdtPooledNode {
  run: number;
  level: string;
  values: readonly string[];
  /** Width its share of linear demand earned, cm. */
  sliceCm: number;
  /** Width one column of it needs: widest pack × minFacingsPerShelf, cm. */
  columnFloorCm: number;
  skuCount: number;
  skuIds: readonly string[];
  shelfCount: number;
  reasons: readonly CdtPoolReason[];
  mergedInto: readonly string[];
}

export interface CdtBuildReport {
  levels: readonly string[];
  inert: readonly string[];
  roles: readonly { level: string; role: "vertical" | "horizontal" | "sequence" }[];
  windows: readonly CdtWindow[];
  pooled: readonly CdtPooledNode[];
  dropped: readonly string[];
  warnings: readonly string[];
}

interface Pack {
  skuId: string;
  attrs: Record<string, string | null>;
  unit: number;
  facings: number;
  w: number;
  h: number;
  est: number;
  /** Not 0 when unstated: the tie-break sorts ascending, so 0 reads as lightest. */
  kg: number | null;
  shelf: number;
  winId: number;
}

/**
 * NOT a comparator: "equal to everything" is intransitive, so with
 * [10kg, unknown, 1kg] `sort` may leave the heavy pack above the light one.
 * Grouped, because weight must not move a pack across a band boundary.
 */
export const orderWeighedWithinGroups = <T extends Pick<Pack, "kg">>(
  packs: T[],
  groupKeyOf: (pack: T) => number,
): T[] => {
  let start = 0;
  for (let end = 1; end <= packs.length; end += 1) {
    if (end < packs.length && groupKeyOf(packs[end]) === groupKeyOf(packs[start])) continue;

    const weighed: { slot: number; pack: T; kg: number }[] = [];
    for (let i = start; i < end; i += 1) {
      const kg = packs[i].kg;
      if (kg !== null) weighed.push({ slot: i, pack: packs[i], kg });
    }
    const sorted = [...weighed].sort((a, b) => a.kg - b.kg);
    weighed.forEach(({ slot }, index) => {
      packs[slot] = sorted[index].pack;
    });

    start = end;
  }

  return packs;
};

const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
const setF = (p: Pack, n: number) => {
  p.facings = Math.max(1, n);
  p.w = +(p.unit * p.facings).toFixed(2);
};

export const poolingKeyOf = (attr: string, value: string): string =>
  `${attr}${PATH_SEPARATOR}${value}`;

export const buildFromCdt = (
  allItems: readonly StudioItem[],
  allRuns: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: CdtLayoutOptions = {},
): { layout: GeneratedLayout; report: CdtBuildReport } => {
  const items = shelfLayoutItemsOf(allItems);
  const runs = shelfLayoutRunsOf(allRuns);
  const vDepth = options.verticalDepth ?? 2;
  const minCol = options.minFacingsPerShelf ?? 2;
  const overrides = options.poolingOverrides ?? {};

  const warnings: string[] = [];
  const pooledNodes: CdtPooledNode[] = [];
  const dropped: Pack[] = [];
  const allWindows: (CdtWindow & { packs: Pack[]; bandPacks: Map<string, Pack[]> })[] = [];

  const levelsIn = draft.spine.map((level) => level.attr);
  const attrsOf = new Map(items.map((item) => [item.skuId, effectiveAttrs(item, draft)]));

  // An inert level carries one value plan-wide and would eat a vertical slot.
  const live = levelsIn.filter(
    (key) =>
      new Set(items.map((item) => attrsOf.get(item.skuId)?.[key]).filter((v) => v && v !== "?"))
        .size > 1,
  );
  const inert = levelsIn.filter((key) => !live.includes(key));
  const vLevels = live.slice(0, vDepth);
  const hLevel: string | undefined = live[vDepth];
  const sLevels = live.slice(vDepth + 1);
  const identityAttr = options.identityAttr ?? vLevels[1] ?? vLevels[0];

  // The parity hook wins; else the draft's declared order, ties by first appearance.
  const rankOf = (
    packs: readonly Pack[],
    attr: string,
    scopePath: readonly string[],
  ): Map<string, number> => {
    if (options.priorityOf) {
      const best = new Map<string, { p: number; n: number }>();
      packs.forEach((pack, n) => {
        const v = String(pack.attrs[attr] ?? "");
        const p = options.priorityOf!(pack.attrs, attr);
        const known = best.get(v);
        if (!known || p < known.p) best.set(v, { p, n });
      });
      return new Map(
        [...best.entries()]
          .sort((a, b) => a[1].p - b[1].p || a[1].n - b[1].n)
          .map(([v], index) => [v, index]),
      );
    }
    const declared =
      draft.scopedValueOrder[scopePath.join(PATH_SEPARATOR)] ?? draft.valueOrder[attr] ?? [];
    const rank = new Map<string, number>();
    declared.forEach((v, index) => rank.set(v, index));
    let next = declared.length;
    for (const pack of packs) {
      const v = String(pack.attrs[attr] ?? "");
      if (!rank.has(v)) rank.set(v, next++);
    }
    return rank;
  };
  const valuesByPriority = (
    packs: readonly Pack[],
    attr: string,
    scopePath: readonly string[],
  ): string[] => {
    const rank = rankOf(packs, attr, scopePath);
    return [...new Set(packs.map((pack) => String(pack.attrs[attr] ?? "")))].sort(
      (a, b) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0),
    );
  };
  const sequenceSort = (packs: readonly Pack[], scopePath: readonly string[]): Pack[] => {
    const seqLevels = sLevels.length ? sLevels : hLevel ? [hLevel] : [];
    const ranks = seqLevels.map((key) => rankOf(packs, key, scopePath));
    return [...packs].sort((a, b) => {
      for (let n = 0; n < seqLevels.length; n++) {
        const key = seqLevels[n];
        const d =
          (ranks[n].get(String(a.attrs[key] ?? "")) ?? 0) -
          (ranks[n].get(String(b.attrs[key] ?? "")) ?? 0);
        if (d) return d;
      }
      return a.w - b.w;
    });
  };

  for (const run of runs) {
    const N = run.shelves.length;
    const RUN = run.widthCm;
    const slack = slacksOf(runBayWidthOf(run, runs));
    const DRIFT = options.driftCm ?? slack.air;
    const byRank = [...run.shelves].sort((a, b) => a.rank - b.rank);
    const openingOf = (shelfRank: number): number => {
      const me = byRank.find((s) => s.rank === shelfRank);
      if (!me) return Infinity;
      const above = byRank.find((s) => s.rank === shelfRank - 1);
      const ceiling = above ? above.yCm - above.thicknessCm : run.heightCm || Infinity;
      return Math.max(ceiling - me.yCm, 0) || Infinity;
    };

    const packs: Pack[] = items
      .filter((item) => item.run === run.run)
      .map((item) => ({
        skuId: item.skuId,
        attrs: attrsOf.get(item.skuId) ?? item.attrs,
        unit: item.facingWidthCm,
        facings: item.facings,
        w: item.facingWidthCm * item.facings,
        h: item.heightCm || 0,
        est: item.salesAmount ?? 0,
        kg: item.weightKg ?? null,
        shelf: 0,
        winId: -1,
      }));
    if (packs.length === 0) continue;

    /* ---- 1. vertical windows, nested over vLevels --------------------- */
    interface Node {
      v: string;
      label: string;
      packs: Pack[];
      demand: number;
      widest: number;
      pooled?: boolean;
      values?: string[];
    }
    let windows: {
      path: string[];
      packs: Pack[];
      x: number;
      width: number;
      pooledKey: string | null;
      pooledValues: string[] | null;
    }[] = [{ path: [], packs, x: 0, width: RUN, pooledKey: null, pooledValues: null }];
    const split = (weights: number[], budget: number) => {
      const t = sum(weights);
      return weights.map((w) => (t > 0 ? (w / t) * budget : budget / weights.length));
    };
    for (const key of vLevels) {
      const next: typeof windows = [];
      for (const win of windows) {
        const vals = valuesByPriority(win.packs, key, win.path);
        const nodes: Node[] = vals.map((v) => {
          const own = win.packs.filter((pack) => String(pack.attrs[key] ?? "") === v);
          return {
            v,
            label: v || "(unmatched)",
            packs: own,
            demand: sum(own.map((pack) => pack.w)),
            widest: Math.max(...own.map((pack) => pack.unit)),
          };
        });
        let w = split(
          nodes.map((n) => n.demand),
          win.width,
        );
        // Pool nodes that cannot be a column: too narrow, or fewer SKUs than
        // shelves. The bound is a fixed 500; nodes.length shrinks on every merge
        // and exits with the tail unpooled.
        for (let pass = 0; pass < 500; pass++) {
          const answer = (j: number) => overrides[poolingKeyOf(key, nodes[j].v)];
          const disqualified = (j: number) =>
            answer(j) === "pooled" ||
            (answer(j) !== "vertical" &&
              (w[j] < nodes[j].widest * minCol || nodes[j].packs.length < N));
          const k = nodes.findIndex((_, j) => disqualified(j));
          if (k < 0 || nodes.length < 2) break;
          // A node kept vertical by answer must remain its own window.
          const adjacent = [k + 1, k - 1].filter(
            (candidate) => candidate >= 0 && candidate < nodes.length,
          );
          const j = adjacent.find((candidate) => answer(candidate) !== "vertical");
          if (j === undefined) break;
          const lo = Math.min(k, j);
          const hi = Math.max(k, j);
          // The one point holding both the node and the numbers that disqualified it.
          pooledNodes.push({
            run: run.run,
            level: key,
            values: nodes[k].values ?? [nodes[k].v],
            sliceCm: +w[k].toFixed(2),
            columnFloorCm: +(nodes[k].widest * minCol).toFixed(2),
            skuCount: nodes[k].packs.length,
            skuIds: nodes[k].packs.map((pack) => pack.skuId),
            shelfCount: N,
            reasons: [
              ...(answer(k) === "pooled" ? (["answered-pooled"] as const) : []),
              ...(w[k] < nodes[k].widest * minCol ? (["too-narrow"] as const) : []),
              ...(nodes[k].packs.length < N ? (["too-few-skus"] as const) : []),
            ],
            mergedInto: nodes[j].values ?? [nodes[j].v],
          });
          nodes[lo] = {
            v: nodes[lo].v,
            label: `${nodes[lo].label} + ${nodes[hi].label}`,
            pooled: true,
            values: [
              ...(nodes[lo].values ?? [nodes[lo].v]),
              ...(nodes[hi].values ?? [nodes[hi].v]),
            ],
            packs: nodes[lo].packs.concat(nodes[hi].packs),
            demand: nodes[lo].demand + nodes[hi].demand,
            widest: Math.max(nodes[lo].widest, nodes[hi].widest),
          };
          nodes.splice(hi, 1);
          w = split(
            nodes.map((n) => n.demand),
            win.width,
          );
        }
        for (let j = 0; j < nodes.length; j++) {
          if (
            overrides[poolingKeyOf(key, nodes[j].v)] === "vertical" &&
            (w[j] < nodes[j].widest * minCol || nodes[j].packs.length < N)
          ) {
            warnings.push(
              `"${nodes[j].v}" kept vertical by request below the column floor — a thin column is a defect the answer accepts`,
            );
          }
        }
        let x = win.x;
        nodes.forEach((n, k) => {
          next.push({
            path: [...win.path, n.label],
            packs: n.packs,
            x,
            width: w[k],
            pooledKey: n.pooled ? key : null,
            pooledValues: n.values ?? null,
          });
          x += w[k];
        });
      }
      windows = next;
    }
    // The windows must tile the run exactly before any pack is placed.
    {
      let edge = 0;
      for (const win of windows) {
        if (Math.abs(win.x - edge) > slack.fit) {
          throw new Error(
            `phase-1 invariant broken: window "${win.path.join(" › ")}" starts at ${win.x.toFixed(3)}, expected ${edge.toFixed(3)} raw units (run ${run.run})`,
          );
        }
        edge += win.width;
      }
      if (Math.abs(edge - RUN) > slack.fit) {
        throw new Error(
          `phase-1 invariant broken: windows tile ${edge.toFixed(3)} of a ${RUN} raw-unit run (run ${run.run})`,
        );
      }
    }
    windows.forEach((win, k) => {
      for (const pack of win.packs) pack.winId = allWindows.length + k;
    });

    /* ---- 2. bands inside each window ---------------------------------- */
    for (const win of windows) {
      // A window holding more than one identity value must band by the identity
      // attr, or the pooled blocks interleave down every shelf.
      const mixedLevel = options.identityAttr
        ? new Set(win.packs.map((pack) => String(pack.attrs[identityAttr] ?? ""))).size > 1
          ? identityAttr
          : null
        : (vLevels.find(
            (key) => new Set(win.packs.map((pack) => String(pack.attrs[key] ?? ""))).size > 1,
          ) ?? null);
      const bandKey = mixedLevel ?? win.pooledKey ?? hLevel ?? null;
      const bandVals = bandKey
        ? win.pooledValues && bandKey === win.pooledKey
          ? valuesByPriority(win.packs, bandKey, win.path).filter((v) =>
              win.pooledValues!.includes(v),
            )
          : valuesByPriority(win.packs, bandKey, win.path)
        : [];
      const nodes = bandVals
        .map((v) => {
          const own = win.packs.filter((pack) => String(pack.attrs[bandKey!] ?? "") === v);
          return { v, packs: own, demand: sum(own.map((pack) => pack.w)) };
        })
        .filter((n) => n.packs.length > 0);

      // need and useful bound the split; more values than shelves merges a pair.
      const need = (n: { packs: Pack[] }) =>
        Math.max(1, Math.ceil(sum(n.packs.map((pack) => pack.unit)) / win.width));
      const useful = (n: { packs: Pack[] }) => Math.max(1, n.packs.length);
      const floorOf = (n: { packs: Pack[] }) => Math.min(need(n), useful(n));
      const merge = (k: number) => {
        nodes.splice(k, 2, {
          v: `${nodes[k].v} + ${nodes[k + 1].v}`,
          packs: nodes[k].packs.concat(nodes[k + 1].packs),
          demand: nodes[k].demand + nodes[k + 1].demand,
        });
      };
      const cheapestPair = () => {
        let k = 0;
        let worst = Infinity;
        for (let j = 0; j < nodes.length - 1; j++) {
          const c = nodes[j].demand + nodes[j + 1].demand;
          if (c < worst) {
            worst = c;
            k = j;
          }
        }
        return k;
      };
      while (nodes.length > N) merge(cheapestPair());
      while (nodes.length > 1 && sum(nodes.map(need)) > N) merge(cheapestPair());

      const share = nodes.map(floorOf);
      while (sum(share) > N) {
        const k = share.indexOf(Math.max(...share));
        if (share[k] <= 1) break;
        share[k] -= 1;
      }
      while (sum(share) < N) {
        let k = -1;
        let worst = -1;
        nodes.forEach((n, j) => {
          if (share[j] >= useful(n)) return;
          const load = n.demand / share[j];
          if (load > worst) {
            worst = load;
            k = j;
          }
        });
        if (k < 0) {
          share[share.indexOf(Math.min(...share))] += 1;
          break;
        }
        share[k] += 1;
      }
      // An orphan shelf is a hole no fill phase repairs, so residue goes to the last band.
      if (nodes.length > 0 && sum(share) < N) {
        share[share.length - 1] += N - sum(share);
      }
      let cursor = 1;
      const bands = nodes.map((n, k) => {
        const shelves: number[] = [];
        for (let j = 0; j < share[k]; j++) shelves.push(byRank[cursor++ - 1]?.rank ?? cursor - 1);
        return { ...n, shelves };
      });
      const bandPacks = new Map<string, Pack[]>();

      /* ---- 3. sequence within each band, then fill -------------------- */
      for (const band of bands) {
        // Band value first: pure sequence order interleaves a merged band's values.
        const bandRank = bandKey
          ? rankOf(band.packs, bandKey, win.path)
          : new Map<string, number>();
        // Weight sorts only within a value's group; contiguity outranks weight.
        // The cursor walks shelves top-down, so ascending kg puts heavy packs low.
        const rankOfPack = (pack: Pack) =>
          bandRank.get(String(pack.attrs[bandKey ?? ""] ?? "")) ?? 0;
        const queue = orderWeighedWithinGroups(
          sequenceSort(band.packs, win.path).sort((a, b) => rankOfPack(a) - rankOfPack(b)),
          rankOfPack,
        );
        // one facing first; growing before placing leaves the tail unplaceable
        for (const pack of queue) setF(pack, 1);
        const target = sum(queue.map((pack) => pack.w)) / band.shelves.length;

        // Non-skipping: scanning forward for a shelf that fits splits a value in two.
        let si = 0;
        let runW = 0;
        const free = new Map(band.shelves.map((s) => [s, win.width]));
        for (const pack of queue) {
          if (runW >= target - slack.fit && si < band.shelves.length - 1) {
            si++;
            runW = 0;
          }
          while (
            si < band.shelves.length - 1 &&
            (pack.h > openingOf(band.shelves[si]) + slack.nudge ||
              pack.w > (free.get(band.shelves[si]) ?? 0) + slack.fill)
          ) {
            si++;
            runW = 0;
          }
          const s = band.shelves[si];
          if (pack.h > openingOf(s) + slack.nudge || pack.w > (free.get(s) ?? 0) + slack.fill) {
            dropped.push(pack);
            continue;
          }
          pack.shelf = s;
          free.set(s, (free.get(s) ?? 0) - pack.w);
          runW += pack.w;
        }
        // No empty shelf inside a band: a hole mid-column is the worst defect.
        for (const s of band.shelves) {
          const liveOn = (x: number) =>
            band.packs.filter((pack) => pack.shelf === x && !dropped.includes(pack));
          if (liveOn(s).length) continue;
          const donor = band.shelves
            .map((x) => ({ x, row: liveOn(x) }))
            .filter((entry) => entry.row.length > 1)
            .sort((a, b) => sum(b.row.map((p) => p.w)) - sum(a.row.map((p) => p.w)))[0];
          if (!donor) {
            warnings.push(`${win.path.join(" / ")} / ${band.v}: sh${s} left empty, no donor`);
            continue;
          }
          const pick = s > donor.x ? donor.row[donor.row.length - 1] : donor.row[0];
          if (pick.h > openingOf(s) + slack.nudge) {
            warnings.push(
              `${win.path.join(" / ")} / ${band.v}: sh${s} too shallow for the donor pack`,
            );
            continue;
          }
          pick.shelf = s;
        }
        // grow to close each shelf, best seller per cm first
        for (const s of band.shelves) {
          const row = band.packs.filter((pack) => pack.shelf === s && !dropped.includes(pack));
          if (!row.length) continue;
          let room = win.width - sum(row.map((pack) => pack.w));
          for (let guard = 0; guard < 400 && room > slack.fill; guard++) {
            const pick = row
              .filter((pack) => pack.unit <= room + slack.fill)
              .sort(
                (a, b) =>
                  b.est / Math.max(slack.edge, b.w) - a.est / Math.max(slack.edge, a.w) ||
                  a.facings - b.facings,
              )[0];
            if (!pick) break;
            setF(pick, pick.facings + 1);
            room -= pick.unit;
          }
        }
        bandPacks.set(band.v, band.packs);
      }

      allWindows.push({
        run: run.run,
        path: win.path,
        x: win.x,
        width: win.width,
        pooled: win.pooledKey !== null,
        bandKey,
        bands: bands.map((b) => ({ value: b.v, shelves: b.shelves })),
        skuIds: win.packs.map((pack) => pack.skuId),
        packs: win.packs,
        bandPacks,
      });
    }

    /* ---- 4. lay x in sequence order, close gaps bounded, seat --------- */
    const runWindows = allWindows.filter((w) => w.run === run.run);
    const liveOf = (w: (typeof runWindows)[number], s: number) =>
      w.packs.filter((pack) => pack.shelf === s && !dropped.includes(pack));
    for (const win of runWindows) {
      for (const shelf of byRank) {
        let x = win.x;
        for (const pack of sequenceSort(liveOf(win, shelf.rank), win.path)) {
          pack.shelf = shelf.rank;
          (pack as Pack & { x?: number }).x = +x.toFixed(2);
          x += pack.w;
        }
      }
    }
    // Spill is bounded by DRIFT per window per shelf; unbounded spill kills blocking.
    if (options.closeGaps !== false) {
      for (const shelf of byRank) {
        const s = shelf.rank;
        let budget =
          RUN -
          sum(
            packs
              .filter((pack) => pack.shelf === s && !dropped.includes(pack))
              .map((pack) => pack.w),
          );
        for (const win of runWindows) {
          const row = liveOf(win, s);
          if (!row.length) continue;
          let room = Math.min(win.width + DRIFT - sum(row.map((pack) => pack.w)), budget);
          for (let guard = 0; guard < 200 && room > slack.fill; guard++) {
            const pick = row
              .filter((pack) => pack.unit <= room + slack.fit)
              .sort(
                (a, b) =>
                  b.est / Math.max(slack.edge, b.w) - a.est / Math.max(slack.edge, a.w) ||
                  a.facings - b.facings,
              )[0];
            if (!pick) break;
            setF(pick, pick.facings + 1);
            room -= pick.unit;
            budget -= pick.unit;
          }
        }
        let x = 0;
        for (const win of runWindows) {
          const row = liveOf(win, s);
          if (!row.length) {
            x = Math.max(x, win.x + win.width);
            continue;
          }
          x = Math.max(x, Math.min(win.x, RUN));
          for (const pack of sequenceSort(row, win.path)) {
            (pack as Pack & { x?: number }).x = +x.toFixed(2);
            x += pack.w;
          }
        }
      }
    }
  }

  /* ---- emit ------------------------------------------------------------ */
  const placements: Record<string, GeneratedPlacement> = {};
  const walkOrder: string[] = [];
  const droppedIds = new Set(dropped.map((pack) => pack.skuId));
  for (const win of allWindows) {
    for (const pack of [...win.packs].sort(
      (a, b) =>
        a.shelf - b.shelf ||
        ((a as Pack & { x?: number }).x ?? 0) - ((b as Pack & { x?: number }).x ?? 0),
    )) {
      if (droppedIds.has(pack.skuId)) continue;
      placements[pack.skuId] = {
        skuId: pack.skuId,
        segments: [
          {
            run: win.run,
            shelf: pack.shelf,
            xCm: (pack as Pack & { x?: number }).x ?? 0,
            hFacings: pack.facings,
          },
        ],
      };
      walkOrder.push(pack.skuId);
    }
  }

  return {
    layout: { placements, walkOrder },
    report: {
      levels: live,
      inert,
      roles: [
        ...vLevels.map((level) => ({ level, role: "vertical" as const })),
        ...(hLevel ? [{ level: hLevel, role: "horizontal" as const }] : []),
        ...sLevels.map((level) => ({ level, role: "sequence" as const })),
      ],
      windows: allWindows.map((win) => ({
        run: win.run,
        path: win.path,
        x: win.x,
        width: win.width,
        pooled: win.pooled,
        bandKey: win.bandKey,
        bands: win.bands,
        skuIds: win.skuIds,
      })),
      pooled: pooledNodes,
      dropped: [...droppedIds],
      warnings,
    },
  };
};
